import type { NavSection } from "@skypointcloud/skypoint-ui";
import { ROUTES } from "../../../components/shared/constant";
import { NAV_IDS, type NavId } from "../sidebar/sidebarConfig";
import { getAllNavRoutes, getNavRoute } from "../sidebar/routeMapping";

/**
 * Pages in the header search.
 *
 * The page list, its labels and who may see what all come from the sidebar
 * (`buildNavSections`), so search can never offer a page the caller's sidebar
 * does not draw, and the route guard would refuse. This file only adds words.
 *
 * - `aliases` are other words for the page itself ("candidates" for Applicants).
 * - `topics` are things that live INSIDE a page under a name nobody would guess
 *   from the sidebar. Nobody looking for "salary types" thinks of Settings > Job
 *   Posting. A topic opens its page. Give it a `route` only when the page
 *   already has its own URL for that part, and only one the page's nav entry
 *   admits in routeMapping's ROUTE_PATTERNS, or the route guard bounces it.
 */
export interface PageTopic {
  label: string;
  aliases?: string[];
  route?: string;
}

export interface PageSearchTerms {
  aliases?: string[];
  topics?: PageTopic[];
  /**
   * Whole queries this page answers first when another page's label matches them just as well:
   * "interview" means Scheduled Interviews, not Interview Guides.
   */
  primaryFor?: string[];
}

export const PAGE_SEARCH_TERMS: Partial<Record<NavId, PageSearchTerms>> = {
  [NAV_IDS.DASHBOARD]: { aliases: ["home", "hiring funnel"] },
  [NAV_IDS.REFERRAL_HUB]: { aliases: ["referral program"] },
  [NAV_IDS.REFERRAL_OPEN_ROLES]: { aliases: ["refer a candidate", "refer a friend"] },
  [NAV_IDS.MY_REFERRALS]: { aliases: ["referral status"] },
  [NAV_IDS.REFERRAL_REWARDS]: { aliases: ["referral bonus"] },
  [NAV_IDS.APPLICANTS]: {
    aliases: ["candidates", "leads", "resumes"],
    topics: [
      { label: "Bulk upload resumes", aliases: ["import resumes"], route: ROUTES.BULK_UPLOADS },
    ],
  },
  [NAV_IDS.CANDIDATE_INBOX]: {
    aliases: ["messages", "replies", "conversations", "candidate email", "candidate sms"],
  },
  [NAV_IDS.JOBS]: { aliases: ["job postings", "openings", "requisitions", "positions"] },
  [NAV_IDS.SCHEDULED_TOURS]: {
    aliases: ["interviews", "calendar", "tours"],
    primaryFor: ["interview", "interviews"],
  },
  [NAV_IDS.TALENT_REDISCOVERY]: { aliases: ["past candidates", "rediscover"] },
  [NAV_IDS.DOCUMENT_CENTRE]: {
    aliases: ["documents", "candidate documents", "sharepoint", "onboarding documents"],
  },
  [NAV_IDS.HRIS_ONBOARDING]: {
    aliases: ["new hires", "joiners", "onboarding checklist"],
  },
  [NAV_IDS.HRIS_OFFBOARDING]: {
    aliases: ["leavers", "exit checklist", "last working day", "employee exit"],
  },
  [NAV_IDS.HRIS_APPROVALS]: {
    aliases: ["onboarding approvals", "waiting for approval", "joiner details"],
  },
  [NAV_IDS.HRIS_SETTINGS]: {
    aliases: [
      "onboarding templates",
      "offboarding templates",
      "onboarding checklist",
      "activation steps",
    ],
  },
  [NAV_IDS.CAREER_PORTALS]: {
    aliases: ["careers page", "career site"],
    topics: [
      { label: "Portal logo and favicon", aliases: ["branding"] },
      { label: "Header slides", aliases: ["banner images"] },
      { label: "Portal SEO", aliases: ["meta title", "meta description", "og image"] },
      { label: "Portal API key" },
      { label: "Portal URL slug", aliases: ["careers link"] },
    ],
  },
  [NAV_IDS.REPORTS]: {
    aliases: ["analytics", "insights", "time to hire"],
    topics: [{ label: "Quick exports", aliases: ["export csv", "download data"] }],
  },
  // skyAgent and skyBrain. The aliases matter more here than elsewhere: half
  // these surfaces were reached from one "SkyAgent" page until recently, so
  // people search for the old name of a thing ("capabilities", "admin") far
  // more often than for the heading it now sits under.
  // The conversation terms were Session History's, which was removed: the
  // Workspace's own list is where past and archived chats are now.
  [NAV_IDS.SKYAGENT_WORKSPACE]: {
    aliases: [
      "skyagent",
      "ai assistant",
      "agent chat",
      "agent conversations",
      "past chats",
      "archived chats",
    ],
  },
  [NAV_IDS.SKYAGENT_AI_EMPLOYEES]: {
    aliases: ["scheduled agent tasks", "scheduled tasks", "cron", "assignments"],
  },
  [NAV_IDS.SKYAGENT_APPROVALS]: {
    aliases: ["pending actions", "agent approvals", "confirm action"],
  },
  [NAV_IDS.SKYAGENT_CONFIGURATION]: {
    aliases: ["agent settings", "skyagent admin", "model settings", "system prompt"],
  },
  [NAV_IDS.SKYBRAIN_OVERVIEW]: { aliases: ["skybrain", "agent knowledge"] },
  [NAV_IDS.SKYBRAIN_MEMORY]: { aliases: ["agent memory", "journal", "what it remembers"] },
  [NAV_IDS.SKYBRAIN_SKILLS]: { aliases: ["agent skills", "agent tools", "capabilities"] },
  // No entry for Quick Prompts: this map is keyed by nav id and the list of
  // pages comes from the sidebar, so terms for a surface with no sidebar row
  // would never be consulted. Its words live on Configuration and Overview,
  // which are where someone reaches it from.
  [NAV_IDS.AI_SCORING]: {
    aliases: ["ai settings"],
    topics: [
      { label: "AI match score rubric", aliases: ["scoring criteria"] },
      {
        label: "What the AI scores on",
        aliases: ["scoring parameters", "match score dimensions"],
      },
    ],
  },
  [NAV_IDS.SURVEYS]: {
    aliases: ["survey", "screening questions", "application form"],
  },
  [NAV_IDS.EMAIL_TEMPLATES]: {
    aliases: ["email templates", "sms templates", "text message templates"],
  },
  [NAV_IDS.EMAIL_SIGNATURE]: { aliases: ["signature"] },
  [NAV_IDS.FEEDBACK_TEMPLATES]: { aliases: ["interview feedback", "scorecard"] },
  [NAV_IDS.AI_INTERVIEW_TEMPLATES]: {
    aliases: ["ai interview questions", "video interview", "culture fit"],
  },
  [NAV_IDS.INTERVIEW_GUIDES]: {
    aliases: ["interview questions", "interview guide", "interview script", "question bank"],
  },
  [NAV_IDS.OFFER_TEMPLATES]: { aliases: ["offer letter", "offer email"] },
  [NAV_IDS.OFFER_WORKFLOWS]: { aliases: ["offer approvals", "approvers"] },
  [NAV_IDS.SENDER_EMAILS]: { aliases: ["from address", "sender address"] },
  [NAV_IDS.LOCATIONS]: { aliases: ["facilities", "sites", "communities", "offices"] },
  [NAV_IDS.PIPELINE]: {
    aliases: ["hiring stages", "pipeline stages"],
    topics: [
      { label: "Stage inactivity alerts", aliases: ["stale candidates"] },
      { label: "Notify candidate on stage change" },
      { label: "Stage feedback form" },
    ],
  },
  [NAV_IDS.CANDIDATE_ENGAGEMENT]: {
    aliases: ["candidate follow-ups", "nurture"],
    topics: [
      { label: "Interview reminders" },
      { label: "Post-interview follow-up" },
      { label: "Offer follow-ups" },
      { label: "Pre-joining countdown" },
      { label: "Unsubscribe link", aliases: ["consent", "opt out"] },
      {
        label: "Engagement rules",
        aliases: ["automation rules"],
        route: ROUTES.CANDIDATE_ENGAGEMENT_RULES,
      },
    ],
  },
  [NAV_IDS.ARCHIVE_REASONS]: { aliases: ["rejection reasons", "disqualification reasons"] },
  [NAV_IDS.RESUME_DIGEST]: { aliases: ["digest email", "top candidates email"] },
  [NAV_IDS.RESUME_TEMPLATES]: { aliases: ["formatted resume", "resume logo"] },
  [NAV_IDS.ANONYMIZATION_CONFIGS]: {
    aliases: ["anonymize", "blind hiring", "redact candidate details"],
  },
  [NAV_IDS.JOB_POSTING_OPTIONS]: {
    topics: [
      { label: "Job types", aliases: ["full time", "part time", "employment type"] },
      { label: "Industries" },
      { label: "Salary types", aliases: ["hourly", "compensation type"] },
    ],
  },
  [NAV_IDS.JOB_TITLES]: { aliases: ["approved job titles", "title list"] },
  [NAV_IDS.ACCESS_MANAGEMENT]: {
    aliases: ["team members", "invite user", "add user", "staff"],
  },
  [NAV_IDS.ROLE_MANAGEMENT]: {
    aliases: ["roles", "permissions", "access control"],
    topics: [{ label: "Sidebar visibility", aliases: ["hide menu items"] }],
  },
  [NAV_IDS.CLIENTS_MANAGEMENT]: { aliases: ["client access", "share with client"] },
  [NAV_IDS.LOGS]: { aliases: ["audit log", "audit trail", "user activity"] },
  [NAV_IDS.PLATFORM]: {
    aliases: ["session timeout", "inactivity timeout", "session expiration", "auto logout"],
  },
  [NAV_IDS.INTEGRATIONS]: {
    aliases: ["connect", "crm"],
    topics: [
      { label: "WelcomeHome" },
      { label: "HubSpot" },
      { label: "Salesforce" },
      { label: "Zapier" },
      { label: "Indeed", aliases: ["job feed"], route: ROUTES.INDEED_INTEGRATION },
      { label: "LinkedIn", route: ROUTES.LINKEDIN_INTEGRATION },
      {
        label: "HRIS",
        aliases: ["employee roster", "hr system", "efficenter"],
        route: ROUTES.HRIS_INTEGRATION,
      },
      {
        label: "greytHR",
        aliases: ["new hire to employee", "employee record"],
        route: ROUTES.GREYTHR_INTEGRATION,
      },
    ],
  },
  [NAV_IDS.CANDIDATE_CONTACT_INFO]: {
    aliases: ["apply by text", "apply by email", "candidate phone number"],
  },
  [NAV_IDS.HELP_PLUS_SUPPORT]: { aliases: ["help", "support ticket", "report a bug"] },
};

export interface PageSearchResult {
  key: string;
  title: string;
  /** Where it lives, outermost first, e.g. ["Settings", "Job Posting"]. */
  trail: string[];
  route: string;
}

export interface PageSearchEntry extends PageSearchResult {
  isPage: boolean;
  labelWords: string[];
  aliasWords: string[];
  trailWords: string[];
  /** PageSearchTerms.primaryFor, as lowercased word strings. */
  primaryFor: string[];
}

/** Lowercased words, punctuation dropped: "AI & Scoring" -> ["ai", "scoring"]. */
function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
}

function entryFor(
  key: string,
  title: string,
  trail: string[],
  route: string,
  isPage: boolean,
  aliases: string[] = [],
  primaryFor: string[] = [],
): PageSearchEntry {
  return {
    key,
    title,
    trail,
    route,
    isPage,
    labelWords: words(title),
    aliasWords: aliases.flatMap(words),
    trailWords: trail.flatMap(words),
    primaryFor: primaryFor.map((q) => words(q).join(" ")),
  };
}

/**
 * Every page the caller's sidebar draws, plus the topics inside them.
 *
 * Only leaf items are pages. A parent like "Settings" is a category in the
 * drawer, not somewhere the route guard lets you land.
 */
export function buildPageSearchIndex(sections: NavSection[]): PageSearchEntry[] {
  const entries: PageSearchEntry[] = [];

  const addPage = (id: string, label: string, trail: string[]) => {
    const navId = id as NavId;
    const route = getNavRoute(navId);
    // The route guard admits a page by these patterns, so an entry without
    // any would open only to be bounced.
    if (!route || getAllNavRoutes(navId).length === 0) return;
    const terms = PAGE_SEARCH_TERMS[navId];
    entries.push(
      entryFor(`page:${id}`, label, trail, route, true, terms?.aliases, terms?.primaryFor),
    );
    for (const topic of terms?.topics ?? []) {
      entries.push(
        entryFor(
          `topic:${id}:${topic.label}`,
          topic.label,
          [...trail, label],
          topic.route ?? route,
          false,
          topic.aliases,
        ),
      );
    }
  };

  for (const section of sections) {
    const sectionTrail = section.header ? [section.header] : [];
    for (const item of section.items) {
      if (!item.children?.length) {
        addPage(item.id, item.label, sectionTrail);
        continue;
      }
      // "Settings" is both the section header and its only item; say it once.
      const trail = item.label === section.header ? sectionTrail : [...sectionTrail, item.label];
      for (const child of item.children) {
        addPage(child.id, child.label, trail);
      }
    }
  }

  return entries;
}

/**
 * Words that carry no meaning in a search box, so "where do I edit
 * departments" searches for "departments".
 */
const FILLER_WORDS = new Set([
  "a",
  "an",
  "and",
  "can",
  "change",
  "do",
  "edit",
  "find",
  "for",
  "how",
  "i",
  "in",
  "is",
  "manage",
  "my",
  "of",
  "set",
  "the",
  "to",
  "where",
]);

/**
 * Words are matched as prefixes, so dropping a plural "s" lets "departments"
 * find "Department" while still finding "Departments".
 */
function singular(word: string): string {
  return word.length > 3 && word.endsWith("s") && !word.endsWith("ss") ? word.slice(0, -1) : word;
}

function queryTokens(query: string): string[] {
  const all = words(query);
  const meaningful = all.filter((w) => !FILLER_WORDS.has(w));
  // A query made only of filler ("edit") is still a search.
  return (meaningful.length ? meaningful : all).map(singular);
}

function editDistance(a: string, b: string): number {
  let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    previous = current;
  }
  return previous[b.length];
}

/**
 * 2 for a clean prefix match, 1 for a one-letter typo ("rubic" for "rubric"),
 * 0 for none. Typos only count on longer words: at three or four letters one
 * edit away matches half the index.
 */
function wordMatch(token: string, word: string): number {
  if (word.startsWith(token)) return 2;
  if (token.length < 5) return 0;
  for (const delta of [-1, 0, 1]) {
    if (editDistance(token, word.slice(0, token.length + delta)) <= 1) return 1;
  }
  return 0;
}

function bestMatch(token: string, candidates: string[]): number {
  let best = 0;
  for (const word of candidates) {
    best = Math.max(best, wordMatch(token, word));
    if (best === 2) break;
  }
  return best;
}

const LABEL_WEIGHT = 3;
const ALIAS_WEIGHT = 2;
const TRAIL_WEIGHT = 1;
/**
 * Tie-breakers, both smaller than the gap between a label and an alias match.
 * A page outranks a topic that matches just as well, even one that starts
 * with the query: "interview" means Scheduled Interviews before Interview
 * reminders. But a topic named for the query still beats a page that only
 * has it as an alias: "logo" means the portal logo, not Resume Templates.
 */
const PAGE_BONUS = 1;
const PREFIX_BONUS = 0.5;
/** Larger than the prefix bonus, smaller than a label/alias gap. */
const PRIMARY_BONUS = 0.75;

/**
 * How well `entry` answers `tokens`, or null when it does not.
 *
 * Every token must match somewhere, except that a query of three or more
 * words may miss one. Typed questions carry words no filler list anticipates.
 * A topic must also match on its own label or aliases: without that rule,
 * "job" would list every topic on the Job Posting page.
 */
function scoreEntry(entry: PageSearchEntry, tokens: string[], query: string): number | null {
  let score = 0;
  let missed = 0;
  let ownMatch = false;

  for (const token of tokens) {
    const label = bestMatch(token, entry.labelWords);
    const alias = bestMatch(token, entry.aliasWords);
    const trail = bestMatch(token, entry.trailWords);
    const best = Math.max(label * LABEL_WEIGHT, alias * ALIAS_WEIGHT, trail * TRAIL_WEIGHT);
    if (best === 0) {
      missed++;
      continue;
    }
    if (label || alias) ownMatch = true;
    score += best;
  }

  const allowedMisses = tokens.length >= 3 ? 1 : 0;
  if (missed > allowedMisses) return null;
  if (!entry.isPage && !ownMatch) return null;

  if (entry.isPage) score += PAGE_BONUS;
  if (entry.labelWords.join(" ").startsWith(words(query).join(" "))) score += PREFIX_BONUS;
  if (entry.primaryFor.includes(words(query).join(" "))) score += PRIMARY_BONUS;
  return score;
}

export const PAGE_SEARCH_LIMIT = 6;

export function searchPages(
  index: PageSearchEntry[],
  query: string,
  limit = PAGE_SEARCH_LIMIT,
): PageSearchResult[] {
  if (query.trim().length < 2) return [];
  const tokens = queryTokens(query);
  if (tokens.length === 0) return [];

  return index
    .map((entry) => ({ entry, score: scoreEntry(entry, tokens, query) }))
    .filter((m): m is { entry: PageSearchEntry; score: number } => m.score !== null)
    .sort(
      (a, b) =>
        b.score - a.score ||
        Number(b.entry.isPage) - Number(a.entry.isPage) ||
        // The shorter title is the closer match: "job" means Jobs first.
        a.entry.title.length - b.entry.title.length ||
        a.entry.title.localeCompare(b.entry.title),
    )
    .slice(0, limit)
    .map(({ entry }) => ({
      key: entry.key,
      title: entry.title,
      trail: entry.trail,
      route: entry.route,
    }));
}
